import { MODEL_BY_ID, SURFACE_REQUIREMENTS, isCompatible } from './aiModels';

export const INSPECTION_SLOT_KEYS = Object.freeze({
  full: Object.freeze([
    'inspection_full_1',
    'inspection_full_2',
    'inspection_full_3',
    'inspection_full_chairman',
  ]),
  strike: Object.freeze([
    'inspection_strike_1',
    'inspection_strike_2',
    'inspection_strike_chairman',
  ]),
  solo: Object.freeze(['inspection_solo']),
});

export const INSPECTION_VARIANTS = Object.freeze(Object.keys(INSPECTION_SLOT_KEYS));
export const REASONING_EFFORTS = Object.freeze([
  'adaptive',
  'none',
  'low',
  'medium',
  'high',
  'xhigh',
  'max',
]);

export const LEGACY_INSPECTION_OVERRIDES_KEY = 'fttx_inspection_model_overrides_v1';
export const INSPECTION_OVERRIDES_KEY = 'fttx_inspection_model_overrides_v2';
export const LEGACY_GPT_MODEL_ID = 'openai/gpt-5.5';
export const GPT_5_6_SOL_ID = 'openai/gpt-5.6-sol';

// These positions used GPT-5.5 as their product default before GPT-5.6 Sol was
// introduced. A GPT-5.5 value is upgraded only while importing v1 and only at
// one of these positions; the same value in v2 is an intentional user choice.
export const LEGACY_GPT_DEFAULT_SLOT_KEYS = Object.freeze([
  'inspection_full_3',
  'inspection_full_chairman',
  'inspection_strike_chairman',
  'inspection_solo',
]);

const ALL_SLOT_KEYS = Object.freeze(Object.values(INSPECTION_SLOT_KEYS).flat());
const LEGACY_GPT_DEFAULT_SLOT_KEY_SET = new Set(LEGACY_GPT_DEFAULT_SLOT_KEYS);

function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasOwn(value, key) {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function hasEveryArrayIndex(value) {
  if (!Array.isArray(value)) return false;
  for (let index = 0; index < value.length; index += 1) {
    if (!hasOwn(value, index)) return false;
  }
  return true;
}

function hasExactKeys(value, expectedKeys) {
  if (!isRecord(value)) return false;
  const keys = Object.keys(value);
  return keys.length === expectedKeys.length && expectedKeys.every((key) => hasOwn(value, key));
}

function isModelCompatibleWithSlot(modelId, slotKey) {
  const model = MODEL_BY_ID[modelId];
  const requirements = SURFACE_REQUIREMENTS[slotKey];
  return Boolean(model && requirements && isCompatible(model, requirements));
}

function assertCanonicalVariantSlots(slots, variant) {
  const expectedKeys = INSPECTION_SLOT_KEYS[variant];
  if (!Array.isArray(slots)
    || slots.length !== expectedKeys.length
    || !hasEveryArrayIndex(slots)) {
    throw new Error(`Inspection variant ${variant} has an invalid slot count`);
  }

  return Array.from({ length: slots.length }, (_unused, index) => {
    const slot = slots[index];
    const expectedSlotKey = expectedKeys[index];
    if (!hasExactKeys(slot, ['slotKey', 'model', 'reasoningEffort'])) {
      throw new Error(`Inspection slot ${expectedSlotKey} is malformed`);
    }
    if (slot.slotKey !== expectedSlotKey) {
      throw new Error(`Inspection slot ${expectedSlotKey} is out of canonical order`);
    }
    if (typeof slot.model !== 'string' || !isModelCompatibleWithSlot(slot.model, expectedSlotKey)) {
      throw new Error(`Inspection slot ${expectedSlotKey} has an unknown or incompatible model`);
    }
    if (!REASONING_EFFORTS.includes(slot.reasoningEffort)) {
      throw new Error(`Inspection slot ${expectedSlotKey} has an invalid reasoning effort`);
    }
    return {
      slotKey: expectedSlotKey,
      model: slot.model,
      reasoningEffort: slot.reasoningEffort,
    };
  });
}

/**
 * Strictly validate the server-owned 17-setting bundle and return a detached,
 * canonical copy. Callers can safely keep this as effective persisted state.
 */
export function normalizeInspectionSettingsBundle(value) {
  if (!hasExactKeys(value, ['concealModelIdentities', 'variants'])) {
    throw new Error('Inspection settings bundle is malformed');
  }
  if (typeof value.concealModelIdentities !== 'boolean') {
    throw new Error('Inspection concealment setting must be boolean');
  }
  if (!hasExactKeys(value.variants, INSPECTION_VARIANTS)) {
    throw new Error('Inspection settings variants are incomplete or malformed');
  }

  const variants = {};
  for (const variant of INSPECTION_VARIANTS) {
    variants[variant] = assertCanonicalVariantSlots(value.variants[variant], variant);
  }

  return {
    concealModelIdentities: value.concealModelIdentities,
    variants,
  };
}

export function createEmptyInspectionOverrides() {
  return Object.fromEntries(
    INSPECTION_VARIANTS.map((variant) => [
      variant,
      new Array(INSPECTION_SLOT_KEYS[variant].length).fill(null),
    ]),
  );
}

function normalizeOverrideGroup(value, variant) {
  const slotKeys = INSPECTION_SLOT_KEYS[variant];
  if (!Array.isArray(value)
    || value.length !== slotKeys.length
    || !hasEveryArrayIndex(value)) {
    throw new Error(`Inspection override group ${variant} has an invalid length`);
  }

  return Array.from({ length: value.length }, (_unused, index) => {
    const modelId = value[index];
    if (modelId === null) return null;
    if (typeof modelId !== 'string' || !isModelCompatibleWithSlot(modelId, slotKeys[index])) {
      throw new Error(`Inspection override for ${slotKeys[index]} is unknown or incompatible`);
    }
    return modelId;
  });
}

/** Validate the complete v2 shape. Missing groups and partial arrays are invalid. */
export function normalizeInspectionOverrides(value) {
  if (!hasExactKeys(value, INSPECTION_VARIANTS)) {
    throw new Error('Inspection override bundle is malformed');
  }

  return Object.fromEntries(
    INSPECTION_VARIANTS.map((variant) => [
      variant,
      normalizeOverrideGroup(value[variant], variant),
    ]),
  );
}

function normalizeLegacyOverrides(value) {
  const empty = createEmptyInspectionOverrides();
  if (!isRecord(value)) return empty;

  // Legacy hooks wrote their groups independently, so absent groups are valid
  // legacy input. Unknown root fields, however, indicate a corrupt shape.
  if (Object.keys(value).some((key) => !INSPECTION_VARIANTS.includes(key))) return empty;

  for (const variant of INSPECTION_VARIANTS) {
    if (!hasOwn(value, variant)) continue;

    let group;
    try {
      group = normalizeOverrideGroup(value[variant], variant);
    } catch (_error) {
      // A corrupt legacy group is discarded without sacrificing another valid
      // independently-written group.
      continue;
    }

    empty[variant] = Array.from({ length: group.length }, (_unused, index) => {
      const modelId = group[index];
      const slotKey = INSPECTION_SLOT_KEYS[variant][index];
      return modelId === LEGACY_GPT_MODEL_ID && LEGACY_GPT_DEFAULT_SLOT_KEY_SET.has(slotKey)
        ? GPT_5_6_SOL_ID
        : modelId;
    });
  }

  return empty;
}

function defaultStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch (_error) {
    return null;
  }
}

function replaceWithEmptyV2(storage, empty) {
  try {
    storage.setItem(INSPECTION_OVERRIDES_KEY, JSON.stringify(empty));
  } catch (_error) {
    // Persistence can be blocked or full. Returning the safe in-memory value is
    // still preferable to reviving legacy overrides.
  }
}

/**
 * Load v2 or perform the sole v1 import. Presence of any v2 value is the
 * migration marker, even when that value is malformed.
 */
export function loadInspectionOverrides(storage = defaultStorage()) {
  const empty = createEmptyInspectionOverrides();
  if (!storage || typeof storage.getItem !== 'function') return empty;

  let currentRaw;
  try {
    currentRaw = storage.getItem(INSPECTION_OVERRIDES_KEY);
  } catch (_error) {
    return empty;
  }

  if (currentRaw !== null) {
    try {
      return normalizeInspectionOverrides(JSON.parse(currentRaw));
    } catch (_error) {
      replaceWithEmptyV2(storage, empty);
      return empty;
    }
  }

  let migrated = empty;
  try {
    const legacyRaw = storage.getItem(LEGACY_INSPECTION_OVERRIDES_KEY);
    if (legacyRaw !== null) migrated = normalizeLegacyOverrides(JSON.parse(legacyRaw));
  } catch (_error) {
    migrated = empty;
  }

  replaceWithEmptyV2(storage, migrated);
  return migrated;
}

export function saveInspectionOverrides(value, storage = defaultStorage()) {
  const normalized = normalizeInspectionOverrides(value);
  if (!storage || typeof storage.setItem !== 'function') {
    throw new Error('Inspection override storage is unavailable');
  }
  storage.setItem(INSPECTION_OVERRIDES_KEY, JSON.stringify(normalized));
  return normalized;
}

function identifyCanonicalSlotGroup(slots) {
  if (!Array.isArray(slots) || !hasEveryArrayIndex(slots)) return null;
  return INSPECTION_VARIANTS.find((variant) => {
    const expected = INSPECTION_SLOT_KEYS[variant];
    return slots.length === expected.length
      && Array.from({ length: slots.length }, (_unused, index) => {
        const slot = slots[index];
        return isRecord(slot) && slot.slotKey === expected[index];
      }).every(Boolean);
  }) || null;
}

/**
 * Resolve the card identities and optional kickoff override. The server owns
 * global defaults; a nullable local group only produces a request array when
 * at least one local override is intentional. Concealment suppresses it all.
 */
export function resolveInspectionModels({ conceal, slots, overrides }) {
  const variant = identifyCanonicalSlotGroup(slots);
  const hasInvalidSlot = variant && Array.from({ length: slots.length }, (_unused, index) => {
    const slot = slots[index];
    return typeof slot.model !== 'string' || !isModelCompatibleWithSlot(slot.model, slot.slotKey);
  }).some(Boolean);
  if (!variant || hasInvalidSlot) {
    throw new Error('Inspection slots are malformed or incompatible');
  }

  const globalModels = Array.from({ length: slots.length }, (_unused, index) => slots[index].model);
  if (conceal !== false) {
    return { effectiveModels: globalModels, requestModels: undefined };
  }

  const local = overrides === undefined
    ? new Array(slots.length).fill(null)
    : normalizeOverrideGroup(overrides, variant);
  const hasLocalOverride = local.some((modelId) => modelId !== null);
  const effectiveModels = globalModels.map((modelId, index) => local[index] || modelId);

  return {
    effectiveModels,
    requestModels: hasLocalOverride ? [...effectiveModels] : undefined,
  };
}

/** Add canonical positional keys to pre-slot-key callback history. */
export function normalizeCallbackSlots(items, slotKeys) {
  if (!Array.isArray(items)
    || !Array.isArray(slotKeys)
    || items.length !== slotKeys.length
    || !hasEveryArrayIndex(items)
    || !hasEveryArrayIndex(slotKeys)) {
    throw new Error('Inspection callback slot cardinality is invalid');
  }
  if (slotKeys.length === 0) throw new Error('Inspection callback slots are empty');

  const canonicalGroup = Object.values(INSPECTION_SLOT_KEYS)
    .find((group) => group.includes(slotKeys[0]));
  const firstIndex = canonicalGroup?.indexOf(slotKeys[0]) ?? -1;
  const canonicalSequence = canonicalGroup
    && Array.from({ length: slotKeys.length }, (_unused, index) => (
      canonicalGroup[firstIndex + index] === slotKeys[index]
    )).every(Boolean);
  const hasUnknownSlot = Array.from({ length: slotKeys.length }, (_unused, index) => (
    !ALL_SLOT_KEYS.includes(slotKeys[index])
  )).some(Boolean);
  if (!canonicalSequence || hasUnknownSlot) {
    throw new Error('Inspection callback slot keys are unknown, duplicated, or out of order');
  }

  return Array.from({ length: items.length }, (_unused, index) => {
    const item = items[index];
    if (!isRecord(item)) {
      throw new Error(`Inspection callback item at position ${index} is malformed`);
    }
    const slotKey = slotKeys[index];
    if (hasOwn(item, 'slot_key') && item.slot_key !== slotKey) {
      throw new Error(`Inspection callback slot mismatch at position ${index}`);
    }
    return { ...item, slot_key: slotKey };
  });
}

export function toPersianDigits(value) {
  return String(value).replace(/[0-9]/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)]);
}

export function getSlotPresentation({ conceal, slotIndex, modelId }) {
  if (!Number.isInteger(slotIndex) || slotIndex < 0) {
    throw new Error('Inspection slot index must be a non-negative integer');
  }
  if (conceal !== false) {
    return { label: `مدل شماره ${toPersianDigits(slotIndex + 1)}` };
  }
  const model = MODEL_BY_ID[modelId];
  return {
    label: model?.label || modelId,
    model,
    logo: model?.logoComponent || null,
  };
}
